/*
 * Panelin menü haritası ve izin eşlemesi — tek harita.
 *
 * Sol menü, rota koruması ve Yetkililer & Roller ekranının yetki listesi bu
 * dosyayı okur. İkinci bir kopya yazılsaydı ayrışırdı: menüye eklenen yeni
 * bir sayfa rol ekranında hiç görünmez, yönetici o sayfayı açmak için hangi
 * yetkiyi vereceğini bilemezdi.
 *
 * Menü **işe göre** gruplanmıştır ve Genel Ayarlar sayfası yoktur: sekmeleri
 * ait oldukları gruba dağılır (sekme alt kümesiyle açılan `Settings.jsx`,
 * bkz. v2/routes.jsx). Eski menünün adresleri (`/admin/settings`,
 * `/admin/live-casino-control` …) kaldırıldı (16.10.2026); yer imleri ve
 * eski bağlantılar `LEGACY_REDIRECTS` ile yeni sayfaya taşınır.
 */
import {
  LayoutDashboard, Users, Gamepad2, Coins, Gift, GalleryHorizontal,
  Search, Settings, UserPlus, TicketPercent, FileClock, ShieldCheck, Landmark,
  PanelBottom, PanelLeft, UserCog, Lock, RadioTower, Disc3,
  Sparkles, BookOpen, Ticket, Anchor as AnchorIcon, Globe2, MessagesSquare,
  Handshake, Megaphone, Rocket, HandCoins, Activity, Target, LayoutList, PartyPopper,
  ArrowDownCircle, ArrowUpCircle, Plug, Wallet, Scale, Trophy, TrendingUp,
  Palette, Code2, BellRing, DatabaseBackup, Mail, RotateCcw, Network,
} from 'lucide-react';
import { openAdminGuide } from './guide';

export const ADMIN_NAVIGATION = [
  {
    id: 'overview',
    label: 'Genel Bakış',
    icon: LayoutDashboard,
    items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, permission: 'dashboard.read' },
      { to: '/admin/yatirim-talepleri', label: 'Yatırım Talepleri', icon: ArrowDownCircle, permission: 'finance.read', badge: 'pending-deposit' },
      { to: '/admin/cekim-talepleri', label: 'Çekim Talepleri', icon: ArrowUpCircle, permission: 'finance.read', badge: 'pending-withdraw' },
    ],
  },
  {
    id: 'finance',
    label: 'Finans',
    icon: Coins,
    items: [
      { to: '/admin/finance', label: 'Finans Geçmişi', icon: Coins, permission: 'finance.read' },
      { to: '/admin/manual-transactions', label: 'Manuel İşlemler', icon: HandCoins, permission: 'finance.read' },
      { to: '/admin/payment-methods', label: 'Ödeme Yöntemleri', icon: Landmark, permission: 'finance.read' },
      { to: '/admin/odeme-saglayicilari', label: 'Ödeme Sağlayıcıları', icon: Wallet, permission: 'finance.read' },
      { to: '/admin/cekim-kurallari', label: 'Çekim Kuralları', icon: Scale, permission: 'finance.read' },
    ],
  },
  {
    id: 'members',
    label: 'Üyeler',
    icon: Users,
    items: [
      { to: '/admin/users', label: 'Kullanıcılar', icon: Users, permission: 'users.read' },
      { to: '/admin/kyc', label: 'KYC Onayları', icon: ShieldCheck, permission: 'users.write' },
      { to: '/admin/search', label: 'Geri Arama Talepleri', icon: Search, permission: 'content.read' },
      { to: '/admin/chat', label: 'Sohbet Moderasyonu', icon: MessagesSquare, permission: 'content.read', theme: 'herkul' },
    ],
  },
  {
    id: 'casino',
    label: 'Casino',
    icon: Gamepad2,
    items: [
      { to: '/admin/games', label: 'Oyunlar', icon: Gamepad2, permission: 'games.read' },
      { to: '/admin/oyun-servisleri', label: 'Oyun Servisleri', icon: RadioTower, permission: 'games.read' },
      { to: '/admin/oyun-ayarlari', label: 'Oyun Ayarları', icon: Network, permission: 'games.read' },
    ],
  },
  {
    id: 'sports',
    label: 'Spor',
    icon: Trophy,
    items: [
      { to: '/admin/sports-bets', label: 'Kuponlar', icon: Ticket, permission: 'finance.read' },
      { to: '/admin/spor-saglayicilari', label: 'Spor Sağlayıcıları', icon: Trophy, permission: ['games.read', 'content.read'] },
      { to: '/admin/oran-cashout', label: 'Oran & Cashout', icon: TrendingUp, permission: 'games.read' },
    ],
  },
  {
    id: 'bonus',
    label: 'Bonus & Promosyon',
    icon: Gift,
    items: [
      { to: '/admin/bonuses', label: 'Bonus Kampanyaları', icon: Gift, permission: ['bonus.read', 'content.read'] },
      { to: '/admin/toplu-bonus', label: 'Toplu Bonus', icon: Users, permission: ['bonus.write', 'finance.write'] },
      { to: '/admin/kayip-bonusu', label: 'Kayıp Bonusu', icon: RotateCcw, permission: 'content.read' },
      { to: '/admin/freespin', label: 'Freespin', icon: Sparkles, permission: 'promo.read' },
      { to: '/admin/promo-codes', label: 'Promosyon Kodları', icon: TicketPercent, permission: 'promo.read' },
    ],
  },
  {
    id: 'events',
    label: 'Etkinlikler',
    icon: PartyPopper,
    items: [
      { to: '/admin/wheel', label: 'Şans Çarkı', icon: Disc3, permission: 'promo.read' },
      { to: '/admin/anchor', label: 'Çapa', icon: AnchorIcon, permission: 'promo.read' },
      { to: '/admin/cekilisler', label: 'Çekilişler', icon: Ticket, permission: 'promo.read' },
      { to: '/admin/tahmin-etkinlikleri', label: 'Tahmin Etkinlikleri', icon: Target, permission: 'promo.read' },
    ],
  },
  {
    id: 'marketing',
    label: 'Pazarlama',
    icon: Megaphone,
    items: [
      { to: '/admin/affiliate', label: 'Affiliate', icon: UserPlus, permission: 'affiliate.read' },
      { to: '/admin/sponsorluklar', label: 'Sponsorluklar', icon: Handshake, permission: 'content.read' },
      { to: '/admin/reklam-kodlari', label: 'Reklam Kodları', icon: Megaphone, permission: 'content.read' },
      { to: '/admin/toplu-mail', label: 'Toplu Mail', icon: Mail, permission: 'content.read' },
    ],
  },
  {
    id: 'site',
    label: 'Site Görünümü',
    icon: Palette,
    items: [
      { to: '/admin/tema-marka', label: 'Tema & Marka', icon: Palette, permission: 'content.read' },
      { to: '/admin/ana-sayfa-duzeni', label: 'Ana Sayfa Düzeni', icon: LayoutList, permission: 'content.read' },
      { to: '/admin/slider', label: 'Banner & Kartlar', icon: GalleryHorizontal, permission: 'content.read' },
      { to: '/admin/karsilama-penceresi', label: 'Karşılama Penceresi', icon: PartyPopper, permission: 'content.read' },
      { to: '/admin/merit-side-panel', label: 'Yan Panel', icon: PanelLeft, permission: 'content.read', theme: ['merit', 'betrun'] },
      { to: '/admin/footer-yonetimi', label: 'Footer', icon: PanelBottom, permission: 'content.read' },
      { to: '/admin/ozel-kod', label: 'Özel Kod', icon: Code2, permission: 'content.read' },
    ],
  },
  {
    id: 'settings',
    label: 'Ayarlar',
    icon: Settings,
    items: [
      { to: '/admin/site-ayarlari', label: 'Site Ayarları', icon: Globe2, permission: 'content.read' },
      { to: '/admin/iletisim-kanallari', label: 'İletişim Kanalları', icon: BellRing, permission: 'content.read' },
      { to: '/admin/alan-adlari', label: 'Alan Adları', icon: Globe2, permission: 'admin.manage' },
      { to: '/admin/entegrasyon-api', label: 'Entegrasyon API', icon: Plug, permission: 'admin.manage' },
      { to: '/admin/yedekleme', label: 'Yedekleme', icon: DatabaseBackup, permission: 'admin.manage' },
      { to: '/admin/license', label: 'Lisans', icon: ShieldCheck, permission: 'admin.manage' },
    ],
  },
  {
    id: 'admin',
    label: 'Yönetim & Denetim',
    icon: UserCog,
    items: [
      { to: '/admin/roles', label: 'Yetkililer & Roller', icon: UserCog, permission: 'admin.manage' },
      { to: '/admin/audit-log', label: 'İşlem Geçmişi', icon: FileClock, permission: 'audit.read' },
      { to: '/admin/technical', label: 'Sistem & İstekler', icon: Activity, permission: 'admin.manage' },
    ],
  },
  {
    id: 'help',
    label: 'Yardım & Hesap',
    icon: BookOpen,
    items: [
      { key: 'admin-guide', label: 'Kullanım Kılavuzu', icon: BookOpen, action: openAdminGuide, internal: true },
      { to: '/admin/yenilikler', label: 'Yenilikler', icon: Rocket, badge: 'updates', internal: true },
      { to: '/admin/security', label: 'Hesap Güvenliği', icon: Lock },
    ],
  },
];


/**
 * Kaldırılan eski menü adresleri → karşılığı olan yeni sayfa. Yer imi,
 * kılavuzdaki eski bağlantı ya da asistanın önerdiği adres 404 yerine doğru
 * sayfayı açar.
 */
export const LEGACY_REDIRECTS = {
  '/admin/settings': '/admin/site-ayarlari',
  '/admin/live-casino-control': '/admin/oyun-servisleri',
  '/admin/flexrix': '/admin/spor-saglayicilari?kitap=flexrix&bolum=yonetim',
  '/admin/emsports': '/admin/spor-saglayicilari?kitap=emsports&bolum=yonetim',
  '/admin/sportdigi': '/admin/spor-saglayicilari?kitap=sportdigi&bolum=yonetim',
  '/admin/footer': '/admin/footer-yonetimi?sekme=menu',
  '/admin/footer-pages': '/admin/footer-yonetimi?sekme=sayfalar',
  '/admin/addresses': '/admin/alan-adlari?sekme=sabit',
  '/admin/giris-adresleri': '/admin/alan-adlari?sekme=giris',
};

// Menüdeki izinler aynı zamanda rota koruması olarak kullanılır; menüde
// görünmeyen bir sayfaya adres çubuğundan da girilemez.
export const ROUTE_PERMISSIONS = ADMIN_NAVIGATION.reduce((map, group) => {
  group.items.forEach((item) => {
    if (item.to) map[item.to] = item.permission;
  });
  return map;
}, {});

/** Adresin bulunduğu grup ve madde (üst bardaki konum satırı için). */
export const locateNavItem = (path) => {
  for (const group of ADMIN_NAVIGATION) {
    const item = group.items.find((entry) => entry.to && (
      entry.end ? entry.to === path : path === entry.to || path.startsWith(`${entry.to}/`)
    ));
    if (item) return { group, item };
  }
  return null;
};

// Detay rotaları menüde yer almaz (üye profili, kupon detayı) ama korumasız
// kalmamalıdır: adres çubuğundan girilen yetkisiz sayfa, backend 403 dönse
// bile boş bir ekran olarak açılır ve yönetici "panel bozuk" sanır. Koruma
// menüdeki üst rotanın izninden devralınır.
export const ROUTE_PREFIX_PERMISSIONS = [
  ['/admin/users/', 'users.read'],
  ['/admin/sports-bets/', 'finance.read'],
];

/** Şemaya bağlı madde verilen şemada görünür mü (`theme` tek değer ya da dizi). */
const themeMatches = (item, siteTheme) => (
  !item.theme || [].concat(item.theme).includes(siteTheme)
);

export const adminNavItemVisible = (item, can, siteTheme) => (
  can(item.permission)
  && themeMatches(item, siteTheme)
  && !(item.internal && can.affiliateOnly)
);

/** Menü maddesinin izin listesi; tek izin de dizi olarak döner. */
const itemPermissions = (item) => {
  if (!item.permission) return [];
  return Array.isArray(item.permission) ? item.permission : [item.permission];
};

/**
 * İzin anahtarı başına, o iznin **tek başına** açtığı menü sayfaları.
 *
 * Bir maddenin izni dizi olduğunda kapı "en az biri" ile açılır
 * (`buildPermissionChecker`), yani her izin o sayfayı kendi başına açar ve
 * madde hepsinin listesine girer. Rol ekranı bu haritayla "bu yetki hangi
 * sayfaları açar" sorusunu yanıtlar; liste elle yazılsaydı menüye eklenen
 * yeni sayfa orada hiç görünmezdi.
 */
export const menuItemsByPermission = (navigation = ADMIN_NAVIGATION) => {
  const map = new Map();
  navigation.forEach((group) => {
    group.items.forEach((item) => {
      itemPermissions(item).forEach((permission) => {
        if (!map.has(permission)) map.set(permission, []);
        map.get(permission).push({
          to: item.to,
          label: item.label,
          icon: item.icon,
          group: group.label,
          theme: item.theme || null,
          shared: itemPermissions(item).length > 1,
        });
      });
    });
  });
  return map;
};

/** İzinden bağımsız, panele girebilen her yetkiliye açık menü maddeleri. */
export const openMenuItems = (navigation = ADMIN_NAVIGATION) => (
  navigation.flatMap((group) => group.items
    .filter((item) => !item.permission)
    .map((item) => ({ ...item, group: group.label })))
);

/**
 * Verilen izin denetleyicisiyle görünecek menü ağacı.
 *
 * Rol ekranındaki önizleme bunu okur: yönetici, kaydetmeden önce hesabın sol
 * menüsünü olduğu gibi görür. Boş grup düşürülmez — `open` bayrağı ile
 * bildirilir, çünkü önizlemenin işi kapalı kalanı da göstermektir.
 */
export const navigationAccess = (can, siteTheme) => ADMIN_NAVIGATION.map((group) => ({
  id: group.id,
  label: group.label,
  items: group.items
    .filter((item) => themeMatches(item, siteTheme))
    .map((item) => ({
      key: item.to || item.key,
      label: item.label,
      icon: item.icon,
      permission: item.permission || null,
      open: (!item.permission || can(item.permission))
        && !(item.internal && can.affiliateOnly),
    })),
})).map((group) => ({
  ...group,
  openCount: group.items.filter((item) => item.open).length,
}));

export default adminNavItemVisible;
